<template>
  <v-dialog v-model="dialogVisible" max-width="600" persistent>
    <v-card class="import-goals-modal">
      <v-card-title class="import-goals-modal__header">
        <v-icon left>mdi-file-import</v-icon>
        Import Goals from ProBim Explorer
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="import-goals-modal__content">
        <!-- File Selection Step -->
        <div
          v-if="!parsedGoals || parsedGoals.length === 0"
          class="file-upload-section"
        >
          <v-file-input
            v-model="selectedFile"
            accept=".json"
            label="Select ProBim Explorer JSON file"
            prepend-icon="mdi-file-json"
            @change="handleFileSelect"
            outlined
          ></v-file-input>

          <div v-if="fileError" class="error-message">
            <v-icon small color="error">mdi-alert-circle</v-icon>
            {{ fileError }}
          </div>
        </div>

        <!-- Goals Review Step -->
        <div v-else class="goals-review-section">
          <h4>
            Found {{ parsedGoals.length }} goal{{
              parsedGoals.length > 1 ? "s" : ""
            }}
          </h4>

          <div class="goals-list">
            <div
              v-for="goal in parsedGoals"
              :key="goal.id"
              :class="{ 'goal-item': true, exists: goal.exists }"
            >
              <v-checkbox
                v-if="!goal.exists"
                :model-value="selectedGoalsToAdd.includes(goal.id)"
                @update:model-value="toggleGoalSelection(goal.id)"
                class="checkbox-item"
                color="primary"
              ></v-checkbox>

              <span class="goal-text">
                {{ goal.description }}
              </span>

              <v-chip
                v-if="goal.exists"
                size="small"
                color="warning"
                text-color="white"
                class="ml-2"
              >
                Already exists
              </v-chip>
            </div>
          </div>

          <div class="info-text">
            <v-icon small color="info">mdi-information</v-icon>
            Goals that already exist are marked and will be skipped.
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="import-goals-modal__actions">
        <v-btn text @click="close"> Cancel </v-btn>
        <v-btn
          v-if="parsedGoals && parsedGoals.length > 0"
          text
          @click="resetModal"
        >
          Choose different file
        </v-btn>
        <v-spacer></v-spacer>

        <v-btn
          v-if="parsedGoals && parsedGoals.length > 0"
          color="primary"
          :disabled="selectedGoalsToAdd.length === 0 || importing"
          :loading="importing"
          @click="importGoals"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          Add {{ selectedGoalsToAdd.length }} Goal(s)
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import apiClient from "@/plugins/apiService";

export default {
  name: "ImportGoalsModal",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    existingGoals: {
      type: Array,
      default: () => [],
    },
    selectedProblem: {
      type: [String, Number],
      required: true,
    },
  },

  emits: ["close", "goals-imported"],

  data() {
    return {
      selectedFile: null,
      parsedGoals: null,
      selectedGoalsToAdd: [],
      fileError: "",
      importing: false,
    };
  },

  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("close", val);
      },
    },
  },

  methods: {
    handleFileSelect() {
      this.fileError = "";

      if (!this.selectedFile || this.selectedFile.length === 0) {
        this.parsedGoals = null;
        return;
      }

      // Get the file from v-model binding
      const selectedFileObj = Array.isArray(this.selectedFile)
        ? this.selectedFile[0]
        : this.selectedFile;

      if (!selectedFileObj) {
        this.parsedGoals = null;
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);
          this.extractGoalsFromJson(jsonData);
        } catch (error) {
          this.fileError = "Invalid JSON file: " + error.message;
          this.parsedGoals = null;
        }
      };

      reader.onerror = () => {
        this.fileError = "Error reading file";
        this.parsedGoals = null;
      };

      reader.readAsText(selectedFileObj);
    },

    extractGoalsFromJson(jsonData) {
      try {
        const sdi_list = jsonData.SDIs || [];
        if (typeof sdi_list !== "object" || sdi_list.length === 0) {
          this.fileError = "Invalid file format: No SDIs found";
          this.parsedGoals = null;
          return;
        }

        // Get unique goal types and check if they already exist
        const existingGoalDescriptions = this.existingGoals.map((g) =>
          g.description.toLowerCase()
        );

        const uniqueGoals = [];
        const seenTypes = new Set();

        // Loop through all SDIs
        for (let i = 0; i < sdi_list.length; i++) {
          const sdi = sdi_list[i];

          if (!sdi || !sdi.elements) {
            continue;
          }

          // Extract elements with level="Goal"
          const goalElements = sdi.elements.filter(
            (element) => element.level === "Goal"
          );

          // Process each goal element
          goalElements.forEach((element) => {
            const goalType = element.type;

            if (!seenTypes.has(goalType)) {
              seenTypes.add(goalType);
              const exists = existingGoalDescriptions.includes(
                goalType.toLowerCase()
              );

              uniqueGoals.push({
                id: goalType,
                description: goalType,
                exists,
              });
            }
          });
        }

        if (uniqueGoals.length === 0) {
          this.fileError = "No goals found in the file";
          this.parsedGoals = null;
          return;
        }

        this.parsedGoals = uniqueGoals;
        // Pre-select only new goals (not existing ones)
        this.selectedGoalsToAdd = uniqueGoals
          .filter((g) => !g.exists)
          .map((g) => g.id);
      } catch (error) {
        this.fileError = "Error processing file: " + error.message;
        this.parsedGoals = null;
      }
    },

    toggleGoalSelection(goalId) {
      const index = this.selectedGoalsToAdd.indexOf(goalId);
      if (index > -1) {
        this.selectedGoalsToAdd.splice(index, 1);
      } else {
        this.selectedGoalsToAdd.push(goalId);
      }
    },

    async importGoals() {
      this.importing = true;

      try {
        const goalsToAdd = this.parsedGoals.filter((g) =>
          this.selectedGoalsToAdd.includes(g.id)
        );

        const responses = await Promise.all(
          goalsToAdd.map((goal) =>
            apiClient.post(`/goal`, {
              description: goal.description,
              problem_id: this.selectedProblem,
            })
          )
        );

        const allSuccessful = responses.every(
          (response) => response.status === 200 || response.status === 201
        );

        if (allSuccessful) {
          this.$emit("goals-imported", goalsToAdd);
          this.close();
        } else {
          this.fileError = "Some goals failed to import";
        }
      } catch (error) {
        this.fileError = "Error importing goals: " + error.message;
      } finally {
        this.importing = false;
      }
    },

    resetModal() {
      this.selectedFile = null;
      this.parsedGoals = null;
      this.selectedGoalsToAdd = [];
      this.fileError = "";
    },

    close() {
      this.resetModal();
      this.dialogVisible = false;
    },
  },
};
</script>

<style scoped>
.import-goals-modal__header {
  background-color: var(--primaryColor);
  color: var(--backgroundColor) !important;
  align-items: center;
}

.goals-review-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.goals-review-section h4 {
  margin: 0;
}

.goals-list {
  display: flex;
  flex-direction: column;
  max-height: 40vh;
  overflow-y: auto;
  gap: 18px;
}

.goal-item {
  display: flex;
  align-items: center;
  border-radius: 4px;
}

.goal-text {
  flex: 1;
}

.goal-item.exists .goal-text {
  text-decoration: line-through;
  opacity: 0.6;
  padding-left: 38px;
}

.goal-item input[type="checkbox"] {
  margin-right: 12px;
  width: 20px;
  height: 20px;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: rgba(244, 67, 54, 0.1);
  border-left: 3px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  font-size: 0.875rem;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: rgba(33, 150, 243, 0.1);
  border-left: 3px solid #2196f3;
  border-radius: 4px;
  font-size: 0.875rem;
}
</style>
